import { Tower } from './Tower';
import { Monster } from '../monsters/Monster';

export class HeavyCannon extends Tower {
  constructor(scene: Phaser.Scene, x: number, y: number) {
    // damage: 60, attackSpeed: 0.5, range: 180 - high damage, low speed
    super(scene, x, y, 60, 0.5, 180);
    
    // Override visual representation
    this.removeAll();
    this.createCannonVisuals();
  }

  protected override createVisuals(): void {
    // Create heavy cannon base
    const base = this.scene.add.circle(0, 0, 25, 0x444444);
    this.add(base);
    
    // Create large cannon barrel
    const barrel = this.scene.add.rectangle(0, -20, 12, 35, 0x666666);
    this.add(barrel);
    
    // Add cannon muzzle
    const muzzle = this.scene.add.circle(0, -37, 5, 0x333333);
    this.add(muzzle);
    
    // Add reinforcement rings
    const ring1 = this.scene.add.circle(0, 0, 20, 0x555555, 0);
    ring1.setStrokeStyle(2, 0x777777);
    this.add(ring1);
  }

  private createCannonVisuals(): void {
    this.createVisuals();
  }

  protected override fireProjectile(target: Monster, damage: number, isCrit: boolean): void {
    // Create cannon recoil effect
    this.createCannonRecoil();
    
    // Create enhanced cannonball
    const cannonball = this.createEnhancedCannonball(isCrit);
    
    // Calculate trajectory with arc
    const distance = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    const travelTime = (distance / 250) * 1000; // Heavy projectile travels slower
    
    // Create smoke trail
    this.createCannonballTrail(cannonball, travelTime);
    
    // Animate with arc trajectory
    this.animateCannonballWithArc(cannonball, target, travelTime, damage, isCrit);
  }

  private createCannonRecoil(): void {
    if (!this.scene) return;
    // Muzzle flash and smoke
    const muzzleFlash = this.scene.add.circle(this.x, this.y - 15, 12, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: muzzleFlash,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => muzzleFlash.destroy()
    });
    
    // Cannon recoil animation
    const originalY = this.y;
    this.scene.tweens.add({
      targets: this,
      y: originalY + 3,
      duration: 100,
      ease: 'Power2',
      yoyo: true
    });
    
    // Smoke clouds
    for (let i = 0; i < 5; i++) {
      const smoke = this.scene.add.circle(
        this.x + (Math.random() - 0.5) * 15,
        this.y - 10 + (Math.random() - 0.5) * 10,
        4 + Math.random() * 4,
        0x444444,
        0.6
      );
      
      this.scene.tweens.add({
        targets: smoke,
        y: smoke.y - 30,
        x: smoke.x + (Math.random() - 0.5) * 20,
        scaleX: 3,
        scaleY: 3,
        alpha: 0,
        duration: 1000,
        delay: i * 50,
        ease: 'Power2',
        onComplete: () => smoke.destroy()
      });
    }
  }

  private createEnhancedCannonball(isCrit: boolean): Phaser.GameObjects.Container {
    const cannonballContainer = this.scene.add.container(this.x, this.y - 15);
    
    // Main cannonball
    const ball = this.scene.add.circle(0, 0, isCrit ? 6 : 5, isCrit ? 0x883333 : 0x333333);
    cannonballContainer.add(ball);
    
    // Metallic shine
    const shine = this.scene.add.circle(-1, -1, isCrit ? 3 : 2, 0xaaaaaa, 0.7);
    cannonballContainer.add(shine);
    
    // Crit effects
    if (isCrit) {
      const critGlow = this.scene.add.circle(0, 0, 10, 0xff4444, 0.3);
      cannonballContainer.add(critGlow);
      
      // Sparks around cannonball
      for (let i = 0; i < 4; i++) {
        const spark = this.scene.add.rectangle(0, 0, 1, 6, 0xffaa00);
        spark.setRotation((Math.PI * 2 * i) / 4);
        cannonballContainer.add(spark);
      }
    }
    
    // Spinning animation
    this.scene.tweens.add({
      targets: cannonballContainer,
      rotation: Math.PI * 4,
      duration: 2000,
      ease: 'Linear'
    });
    
    return cannonballContainer;
  }

  private createCannonballTrail(cannonball: Phaser.GameObjects.Container, duration: number): void {
    const trailTimer = this.scene.time.addEvent({
      delay: 40,
      repeat: Math.floor(duration / 40),
      callback: () => {
        // Heavy smoke trail
        const smoke = this.scene.add.circle(
          cannonball.x + (Math.random() - 0.5) * 8,
          cannonball.y + (Math.random() - 0.5) * 8,
          2 + Math.random() * 3,
          0x555555,
          0.5
        );
        
        this.scene.tweens.add({
          targets: smoke,
          alpha: 0,
          scaleX: 4,
          scaleY: 4,
          duration: 800,
          ease: 'Power2',
          onComplete: () => smoke.destroy()
        });
        
        // Occasional sparks
        if (Math.random() < 0.3) {
          const spark = this.scene.add.circle(
            cannonball.x,
            cannonball.y,
            1,
            0xffaa00,
            0.8
          );
          
          this.scene.tweens.add({
            targets: spark,
            x: spark.x + (Math.random() - 0.5) * 20,
            y: spark.y + (Math.random() - 0.5) * 20,
            alpha: 0,
            duration: 400,
            ease: 'Power2',
            onComplete: () => spark.destroy()
          });
        }
      }
    });
    
    this.scene.time.delayedCall(duration + 200, () => {
      trailTimer.destroy();
    });
  }

  private animateCannonballWithArc(cannonball: Phaser.GameObjects.Container, target: Monster, travelTime: number, damage: number, isCrit: boolean): void {
    // Calculate arc height
    const distance = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    const arcHeight = Math.min(60, distance * 0.3); // Arc height based on distance
    
    // Arc trajectory using path
    const path = new Phaser.Curves.QuadraticBezier(
      new Phaser.Math.Vector2(this.x, this.y - 15),
      new Phaser.Math.Vector2((this.x + target.x) / 2, this.y - 15 - arcHeight),
      new Phaser.Math.Vector2(target.x, target.y)
    );
    
    // Animate along path
    let pathProgress = 0;
    const tween = this.scene.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: travelTime,
      ease: 'Power2',
      onUpdate: (tween) => {
        pathProgress = tween.getValue() || 0;
        const point = path.getPoint(pathProgress);
        cannonball.x = point.x;
        cannonball.y = point.y;
      },
      onComplete: () => {
        this.createCannonballImpact(target.x, target.y, isCrit, damage);
        
        // Apply damage
        target.takeDamage(damage);
        
        // Show enhanced crit effect
        if (isCrit) {
          this.showEnhancedCritEffect(target.x, target.y);
        }
        
        cannonball.destroy();
      }
    });
  }

  private createCannonballImpact(x: number, y: number, isCrit: boolean, damage: number): void {
    // Ground crater effect
    const crater = this.scene.add.circle(x, y, isCrit ? 18 : 12, 0x222222, 0.8);
    this.scene.tweens.add({
      targets: crater,
      alpha: 0.3,
      duration: 2000,
      onComplete: () => crater.destroy()
    });
    
    // Impact explosion
    const explosion = this.scene.add.circle(x, y, isCrit ? 25 : 18, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: explosion,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
    
    // Dust and debris
    for (let i = 0; i < (isCrit ? 12 : 8); i++) {
      const angle = (Math.PI * 2 * i) / (isCrit ? 12 : 8);
      const distance = isCrit ? 30 : 20;
      
      // Rock debris
      const debris = this.scene.add.rectangle(x, y, 3, 3, 0x666666);
      debris.setRotation(Math.random() * Math.PI * 2);
      
      this.scene.tweens.add({
        targets: debris,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance - 20,
        rotation: debris.rotation + Math.PI * 2,
        alpha: 0,
        duration: 800,
        ease: 'Power2',
        onComplete: () => debris.destroy()
      });
      
      // Dust clouds
      if (i % 2 === 0) {
        const dust = this.scene.add.circle(
          x + Math.cos(angle) * (distance * 0.7),
          y + Math.sin(angle) * (distance * 0.7),
          3 + Math.random() * 3,
          0x888888,
          0.4
        );
        
        this.scene.tweens.add({
          targets: dust,
          scaleX: 4,
          scaleY: 4,
          alpha: 0,
          duration: 1200,
          ease: 'Power2',
          onComplete: () => dust.destroy()
        });
      }
    }
    
    // Heavy impact screen shake
    this.scene.cameras.main.shake(isCrit ? 250 : 200, isCrit ? 0.025 : 0.02);
    
    // Damage number with heavy impact style
    const damageText = this.scene.add.text(x, y - 25, `${damage.toFixed(0)}`, {
      fontSize: isCrit ? '20px' : '18px',
      color: isCrit ? '#ff4444' : '#ffaa00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: damageText,
      y: y - 50,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => damageText.destroy()
    });
  }
} 